/**
 * Provider Actions Hook (New Backend)
 * CRUD mutations for provider profiles via REST endpoints.
 *
 * Follows the same pattern as useServiceActions, useFinanceActions, useAppointmentActions.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createProvider,
  updateProvider,
  deleteProvider,
} from '@/api/providerActions'
import type {
  CreateProviderInput,
  UpdateProviderInput,
} from '@/types/provider'
import { dashboardKeys } from '@/hooks/useDashboardStats'

export function useProviderActions() {
  const queryClient = useQueryClient()

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['organizationProviders'] }),
    ])
  }

  // ── Create provider ────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (input: CreateProviderInput) => {
      const res = await createProvider(input)
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to create provider')
      }
      return res
    },
    onSuccess: async (data) => {
      const name = data.user
        ? `${data.user.first_name} ${data.user.last_name}`.trim()
        : 'Provider'
      toast.success(`Provider "${name}" created successfully`)
      if (data.temporary_password) {
        toast.info(`Temporary password: ${data.temporary_password}`, {
          duration: 10000,
        })
      }
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to create provider', { description: err?.message })
    },
  })

  // ── Update provider ────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: async ({
      providerId,
      input,
    }: {
      providerId: string
      input: UpdateProviderInput
    }) => {
      const res = await updateProvider(providerId, input)
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to update provider')
      }
      return res
    },
    onSuccess: async () => {
      toast.success('Provider updated successfully')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to update provider', { description: err?.message })
    },
  })

  // ── Delete provider ────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (providerId: string) => {
      const res = await deleteProvider(providerId)
      if (!res?.success) {
        throw new Error(res?.message || 'Failed to delete provider')
      }
      return res
    },
    onSuccess: async () => {
      toast.success('Provider deleted successfully')
      await invalidateAll()
    },
    onError: (err: any) => {
      toast.error('Failed to delete provider', { description: err?.message })
    },
  })

  return {
    // Action functions (return void promises for easy chaining)
    createProvider: (input: CreateProviderInput) =>
      createMutation.mutateAsync(input).then(() => {}),
    updateProvider: (providerId: string, input: UpdateProviderInput) =>
      updateMutation.mutateAsync({ providerId, input }).then(() => {}),
    deleteProvider: (providerId: string) =>
      deleteMutation.mutateAsync(providerId).then(() => {}),

    // Mutation objects (for loading states, etc.)
    createMutation,
    updateMutation,
    deleteMutation,

    // Combined loading state
    isLoading:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
  }
}
